import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Button, Field, Groupper, Header, Icon, Input, Label, Message, Table, Toolbar, useFrontier, Checkbox, Modal } from "react-frontier2";
import { CatalogEvent, SetLoading, Venue, VenueSection } from '@arema/components/Classes';
import { Link, useNavigate, useParams } from "react-router-dom";
import { bindChange, bindClick, bindFormChange, fromUnix, getNowLocal } from '@arema/components/Util';
import { useTitle } from '@arema/components/Hooks';
import { DeletedBanner, ErrorHeader, NotFound, SearchVenueSectionModal, UserLog } from '../../components';
import { useUser } from '../../AdminHooks';
import { QRCodeSVG } from 'qrcode.react';
import { Scanner } from '../../AdminClasses';
import { firstBy } from 'thenby';
import Validator from "@arema/components/Validator";
import API from "./../../API";
import SearchEventModal from "../../components/SearchEventModal";
import UserAccess from '../../UserAccess';

enum Modals{
	QRCODE = 1,
	REINITIALIZE = 2,
	RESET_TOKEN = 3,
	SEARCH_EVENT = 4,
	SEARCH_SECTION = 5,
}

var ScannerView = ()=>{
	var { user, hasAccess } =	useUser();
	var { scanner_id } = useParams();
	var { setTitle } = useTitle();
	var { Toast } = useFrontier();
	var [errorPrompts, setErrorPrompts] = useState<string[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [notFound, setNotFound] = useState<boolean>(false);
	var [accessDate, setAccessDate] = useState<boolean>(false);
	var [scanner, setScanner] = useState<Scanner>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [modalConfirm, setModalConfirm] = useState<boolean>(false);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [needsSave, setNeedsSave] = useState<boolean>(false);
	var tokenInputRef = useRef<HTMLInputElement>(null);
	var navigate = useNavigate();
	const IS_CREATE = scanner_id==='create';
	
	var ACCCES = useMemo(()=>{
		return {
			REGEN_TOKEN: hasAccess(UserAccess.CATALOGS.SCANNER_REGEN_TOKEN),
			REINITIALIZE: hasAccess(UserAccess.CATALOGS.SCANNER_REINITIALIZE),
		}
	}, []);

	var onSaveEvents = (event: CatalogEvent): boolean=>{
		if(!scanner) return false;
		var ix = scanner.events.findIndex(a=>a.event_id===event.event_id);
		if(ix!==-1){
			Toast.error('Este checador ya tiene este evento agregado.');
			return false;
		}
		var scn = {...scanner};
		scn.events.push({
			event_id: event.event_id,
			event_name: event.event_name
		});
		setScanner(scn);
		setNeedsSave(true);
		return true;
	}

	var onSectionSelected = (venue: Venue, section: VenueSection)=>{
		if(!scanner) return false;
		var ix = scanner.sections.findIndex(a=>a.section_id===section.section_id);
		if(ix!==-1){
			Toast.error('Este checador ya tiene esta sección agregada');
			return false;
		}
		var scn = {...scanner};
		scn.sections.push({
			section_id: section.section_id,
			section_name: section.section_name,
			venue_id: venue.venue_id,
			venue_name: venue.venue_name,
			city: venue.city,
		});
		setScanner(scn);
		setNeedsSave(true);
		return true;
	}

	useEffect(()=>{
		var id = parseInt(scanner_id);
		if(!isNaN(id)) {
			if(scanner===null || scanner.scanner_id!==id){
				setScanner(null);
				loadData(id);
			}
		}else{
			setTitle('Crear checador');
			if(!scanner){
				setAccessDate(false);
				setScanner({
					scanner_id: null,
					scanner_name: '',
					date_start: null,
					date_end: null,
					all_events: false,
					all_sections: false,
					events: [],
					token: null
				})
			}
		}
	}, [scanner_id]);

	var loadData = async (scanner_id: number)=>{
		API.getScanner(scanner_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			if(!res.data) return setNotFound(true);
			setTitle(`Checador ${res.data.scanner_name}`);
			setScanner(res.data);
			setAccessDate(!!(res.data.date_start || res.data.date_end));
		}).catch(err=>{
			setLoadError('Hubo un error cargando el checador (LCL-1)');
		});
	}

	var deleteEvents = (event_id: number)=>{
		return ()=>{
			var scn = {...scanner};
			scn.events = scn.events.filter(a=>a.event_id!==event_id);
			setScanner(scn);
			setNeedsSave(true);
		}
	}
	
	var deleteSection = (section_id: number)=>{
		return ()=>{
			var scn = {...scanner};
			scn.sections = scn.sections.filter(a=>a.section_id!==section_id);
			setScanner(scn);
			setNeedsSave(true);
		}
	}

	var submit = async (setLoading: (b: boolean)=>void)=>{
		var { valid, prompts } = Validator(scanner, {
			scanner_name: [{ rule: 'length', params: [3, 32], label: 'Nombre' }],
			...(accessDate ? {
				date_start: [{ rule: 'number', label: 'Fecha inicio' }],
				date_end: [{ rule: 'number', label: 'Fecha fin' }]
		 	} : {})
		});
		setErrorPrompts(prompts);
		if(!valid) return;
		var payload: Scanner = {
			scanner_id: scanner.scanner_id,
			scanner_name: scanner.scanner_name,
			date_start: accessDate ? scanner.date_start : null,
			date_end: accessDate ? scanner.date_end : null,
			all_events: scanner.all_events,
			all_sections: scanner.all_sections,
		}
		var events = scanner.events ? scanner.events.map(a=>a.event_id) : [];
		var sections = scanner.sections ? scanner.sections.map(a=>a.section_id) : [];
		setLoading(true);
		if(scanner.scanner_id){
			API.editScanner(payload, events, sections).then(res=>{
				if(res.error) return setErrorPrompts([res.message]);
				Toast.success('Se actualizo el checador');
				setNeedsSave(false);
			}).catch(err=>{
				setErrorPrompts(['Hubo un error inesperado actualizando el checador (LCL-10)']);
			}).finally(()=>{
				setLoading(false);
			});
		}else{
			API.createScanner(payload, events, sections).then(res=>{
				if(res.error) return setErrorPrompts([res.message]);
				Toast.success('Se ha creado el checador!');
				navigate(`/scanners/${res.data.scanner_id}`);
			}).catch(err=>{
				setErrorPrompts(['Hubo un error inesperado creando el checador (LCL-11)']);
			}).finally(()=>{
				setLoading(false);
			});
		}
	}
	
	var showQrcode = ()=>{
		if(!scanner?.scanner_id || !scanner.token) return;
		setShownModal(Modals.QRCODE);
	}

	var showModal = (modal: Modals)=>{
		return ()=>{
			setModalConfirm(false);
			setModalPrompts(null)
			setShownModal(modal);
		}
	}

	var sendReinitialize = (setLoading: SetLoading)=>{
		if(!modalConfirm || !scanner?.scanner_id || !ACCCES.REINITIALIZE) return;
		setLoading(true);
		API.reinitializeScanner(scanner.scanner_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha reinicializado el checador. Los dispositivos conectados a este token se han desconectado.', { duration: 10000 });
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado reinicializando el checador. (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendNewToken = (setLoading: SetLoading)=>{
		if(!modalConfirm || !scanner?.scanner_id || ACCCES.REGEN_TOKEN) return;
		setLoading(true);
		API.resetScannerToken(scanner.scanner_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha generado el nuevo token para el checador. Los dispositivos conectados a este token se han desconectado.', { duration: 10000 });
			setShownModal(null);
			setScanner({
				...scanner,
				token: res.data.token
			});
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado generando el nuevo token para el checador. (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var now = useMemo(()=>{
		return getNowLocal();
	}, [])

	if((Number.isNaN(parseInt(scanner_id)) && !IS_CREATE) || notFound) {
		return <NotFound />
	}
	if(loadError) {
		return <ErrorHeader error={loadError} />
	}
	if(!scanner) {
		return <Header loading text='Cargando checador' />
	}

	var onFormChange = bindFormChange(scanner, setScanner);

	return (
		<div>
			{!!scanner.deleted ? <DeletedBanner date_deleted={scanner.date_deleted} /> : null}
			<Groupper
				title={`${IS_CREATE ? 'Crear' : 'Editar'} checador`}
				style={{ maxWidth: 600, margin: 'auto' }}
				actions={<>
					{needsSave && !IS_CREATE && (
						<Label style={{ marginRight: 5 }}>Falta guardar</Label>
					)}
					<Button text="Guardar" color="black" onClick={submit} />
				</>}
			>
				{!IS_CREATE &&
					<Toolbar fitted>
						{ACCCES.REINITIALIZE && (
							<Toolbar.Item text='Reinicializar' iconName='sync' onClick={showModal(Modals.REINITIALIZE)} />
						)}
						{ACCCES.REGEN_TOKEN && (
							<Toolbar.Item text='Nuevo token' iconName='code' onClick={showModal(Modals.RESET_TOKEN)} />
						)}
					</Toolbar>
				}
				<Input label="Nombre" placeholder="Nombre del checador" value={scanner.scanner_name} onChange={onFormChange('scanner_name')} style={{ marginBottom: 15 }} />
				<Checkbox label={'Este checador tiene fecha de acceso'} onChange={bindChange(setAccessDate)} checked={!!accessDate} style={{ marginBottom: 10 }} />
				{accessDate && (
					<Field amount={2} style={{ marginBottom: 15 }}>
						<Input 
							label="Fecha Inicio" 
							value={scanner.date_start} 
							onChange={onFormChange('date_start')} 
							calendar={{ 
								date: scanner.date_start ? fromUnix(scanner.date_start).toJSDate() : now.toJSDate(), 
								mode: 'date' 
							}} 
						/>
						<Input 
							label="Fecha fin" 
							value={scanner.date_end} 
							onChange={onFormChange('date_end')} 
							calendar={{ 
								date: scanner.date_end ? fromUnix(scanner.date_end).toJSDate() : now.plus({ day: 1 }).toJSDate(),
								mode: 'date' 
							}} 
						/>
					</Field>
				)}
				<Checkbox label={'Este checador puede checar cualquier evento'} onChange={onFormChange('all_events')} checked={!!scanner.all_events} style={{ marginBottom: 10 }} />
				<Checkbox label={'Este checador puede checar cualquier area'} onChange={onFormChange('all_sections')} checked={!!scanner.all_sections} />

				{!!scanner?.scanner_id && (
					<Field label='Token' style={{ marginTop: 15 }} labelStyle={{ textAlign: 'center' }}>
						<Input 
							readonly
							value={`${scanner.scanner_id}s${scanner.token}`} 
							ref={tokenInputRef} 
							inputStyle={{ fontSize: 32, padding: 5, textAlign: 'center', fontWeight: 'bold', fontFamily: 'Roboto Mono' }}
							labelStyle={{ textAlign: 'center' }}
							onClick={()=>{
								tokenInputRef.current?.select();
							}}
							buttons={(
								<Button icon iconName='qrcode' iconStyle={{ lineHeight: '46px', fontSize: 24 }} onClick={showQrcode} />
							)}
						/>
					</Field>
				)}

				{!scanner.all_events && (
					<Table fitted striped
						className='first'
						style={{ marginTop: 15 }}
					>
						<Table.Head
							title='Eventos'
							collapsingIndexes={[0, 2]}
							centeredIndexes={[2]}
							data={[['ID', 'Nombre', <Icon name="remove" />]]}
							titleRight={(
								<Button size='tiny' text='Agregar' iconName='plus-circle' color='black' onClick={bindClick(setShownModal, Modals.SEARCH_EVENT)} />
							)}
						/>
						<Table.Body>
							{(!scanner.events || scanner.events.length===0) && (
								<Table.Cell row empty colSpan={100} value={'No hay eventos asignados a este checador'} />
							)}
							{scanner.events.map(a=>(
								<Table.Row data={[
									a.event_id,
									<Link className='compact' to={`/events/${a.event_id}`} target='_blank'>{a.event_name}</Link>,
									<Button
										iconName='remove'
										color='black'
										size='tiny'
										style={{ minWidth: 40 }}
										onClick={deleteEvents(a.event_id)}
									/>
								]} />
							))}
						</Table.Body>
					</Table>
				)}
				{!scanner.all_sections && (
					<Table fitted
						className='first'
						style={{ marginTop: 15 }}
					>
						<Table.Head
							title='Secciones'
							collapsingIndexes={[0, 1, 3, 4]}
							centeredIndexes={[4]}
							data={[['ID', 'Recinto', 'Nombre', 'Ciudad', <Icon name="remove" />]]}
							titleRight={(
								<Button size='tiny' text='Agregar' iconName='plus-circle' color='black' onClick={bindClick(setShownModal, Modals.SEARCH_SECTION)} />
							)}
						/>
						<Table.Body>
							{(scanner?.sections && scanner?.sections.length>0) ? (
								scanner.sections.sort(firstBy('venue_id').thenBy('section_id')).map(a=>(
									<Table.Row
										collapsingIndexes={[0, 1, 3, 4]}
										centeredIndexes={[4]}
										data={[
											a.section_id,
											<Link className='compact' to={`/venue/${a.venue_id}`} target='_blank'>{a.venue_name}</Link>,
											a.section_name,
											a.city,
											<Button
												iconName='remove'
												color='black'
												size='tiny'
												style={{ minWidth: 40 }}
												onClick={deleteSection(a.section_id)}
											/>
										]} 
									/>
								))
							) : (
								<Table.Cell row empty colSpan={100} value={'No hay secciones asignadas a este checador'} />
							)}
						</Table.Body>
					</Table>
				)}
				<Message type='error' list={errorPrompts} style={{ marginTop: 15 }} />
			</Groupper>
			<Modal open={shownModal===Modals.QRCODE} size='tiny' onClose={bindClick(setShownModal)}>
				<Modal.Header>Token de registro</Modal.Header>
				<Modal.Content>
					<div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 400 }}>
						<div style={{ marginBottom: 10, fontWeight: 'bold', maxWidth: 300, textAlign: 'center' }}>
							Escanea este código en la pantalla de registro del checador
						</div>
						<QRCodeSVG value={`${scanner.scanner_id}s${scanner.token}`} width={300} height={300} />
						<div className='fr mono' style={{ fontSize: 28, fontWeight: 'bold', marginTop: 10 }}>
							{`${scanner.scanner_id}s${scanner.token}`}
						</div>
						<div style={{ color: 'gray' }}>
							Auth #{user.user_id}
						</div>
					</div>
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cerrar' onClick={bindClick(setShownModal)} />
				</Modal.Actions>
			</Modal>
			{ACCCES.REINITIALIZE && (
				<Modal open={shownModal===Modals.REINITIALIZE} size='tiny' onClose={bindClick(setShownModal)}>
					<Modal.Header>Reinicializar checador</Modal.Header>
					<Modal.Content>
						<Header text='¿Reinicializar checador?' style={{ marginBottom: 20 }} />
						<Message type='info' centered>
							Esto desconectará cualquier dispositivo que se haya registrado con este token. Los dispositivos desconectados tendrán que registrarse con un token nuevamente.
						</Message>
						<Checkbox checked={!!modalConfirm} onChange={bindChange(setModalConfirm)} label={'Deseo reinicializar el checador'} style={{ marginTop: 20 }} />
						<Message list={modalPrompts} type='error' />
					</Modal.Content>
					<Modal.Actions>
						<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
						<Button text='Continuar' color='black' disabled={!modalConfirm} onClick={sendReinitialize} />
					</Modal.Actions>
				</Modal>
			)}
			{ACCCES.REGEN_TOKEN && (
				<Modal open={shownModal===Modals.RESET_TOKEN} size='mini' onClose={bindClick(setShownModal)}>
					<Modal.Header>Nuevo token</Modal.Header>
					<Modal.Content>
						<Header text='¿Generar nuevo token?' style={{ marginBottom: 20 }} />
						<Message type='info' centered>
						Esto generará un nuevo token aleatorio para este checador. Los dispositivos que estén conectados a este token serán desconectados.
						</Message>
						<Checkbox checked={!!modalConfirm} onChange={bindChange(setModalConfirm)} label={'Deseo generar un nuevo token para este checador'} style={{ marginTop: 20 }} />
						<Message list={modalPrompts} type='error' />
					</Modal.Content>
					<Modal.Actions>
						<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
						<Button text='Continuar' color='black' disabled={!modalConfirm} onClick={sendNewToken} />
					</Modal.Actions>
				</Modal>
			)}
			{!!scanner.scanner_id && (
				<UserLog user={scanner} />
			)}
			<SearchEventModal
				open={shownModal===Modals.SEARCH_EVENT}
				onClose={bindClick(setShownModal)}
				title='Buscar Evento'
				onEventSelected={onSaveEvents}
			/>
			<SearchVenueSectionModal
				section
				open={shownModal===Modals.SEARCH_SECTION}
				onClose={bindClick(setShownModal)}
				onSectionSelected={onSectionSelected}
			/>
		</div>
	)
}

export default ScannerView;
